<template>
  <v-app :class="{ fullscreen: fullscreen }">
    <a
      v-show="!fullscreen"
      class="skip-link"
      href="#main">
      Skip to main
    </a>
    <Header v-show="!fullscreen" />
    <router-view v-if="locations" id="main" />
    <Footer v-show="!fullscreen" />
  </v-app>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Header from '@/components/Header'
import Footer from '@/components/Footer'

import geocode from '@/mixins/geocode'
import routerCompatibility from '@/mixins/routerCompatibility'

export default {
  components: {
    Header,
    Footer
  },
  mixins: [
    geocode,
    routerCompatibility
  ],
  computed: {
    ...mapGetters([
      'locations',
      'fullscreen',
      'activeLocations',
      'transitStops',
      'campuses',
      'activeCampus',
      'filteredTags'
    ])
  },
  watch: {
    async $route (to, from) {
      if (!from.query.fullscreen && to.query.fullscreen) {
        this.setFullscreen(
          this.$route.query.fullscreen === 'true' ||
          this.$route.query.fullscreen === true
        )
      }

      if (to.params.id && to.params.id !== from.params.id && to.name !== 'directions') {
        const ids = to.params.id.split(',')
        this.onLocationIdChange(ids)
      }

      if (to.query.tag) {
        const tags = (Array.isArray(to.query.tag)) ? to.query.tag : [to.query.tag]
        this.setFilteredTags(tags)
      }
    },
    activeLocations (to, from) {
      if (to === from) return

      const id = this.activeLocations.map(location => location._id).join(',') || undefined

      if (this.$route.name === 'home' || this.$route.name === 'location') {
        if (this.activeLocations.length > 1) {
          this.$router.push({
            name: 'location',
            params: { id },
            query: this.$route.query
          })
        } else if (this.activeLocations.length === 1) {
          this.setSelectedLocation(this.activeLocations[0])

          this.$router.push({
            name: 'location',
            params: {
              id,
              mode: this.$route.params.mode
            },
            query: this.$route.query
          })
        } else {
          this.$router.push({
            name: 'home',
            query: this.$route.query
          })
        }
      }
    },
    locations () {
      // On route ID parameter set
      if (this.$route.params.id) {
        const ids = this.$route.params.id.split(',')
        const prevIds = []
        this.activeLocations.forEach(location => {
          prevIds.push(location._id)
        })

        const identicalIds = ids.length === prevIds.length && ids.sort().every((value, index) => { return value === prevIds.sort()[index] })
        if (!identicalIds) { this.onLocationIdChange(ids) }
      }
    },
    filteredTags () {
      this.$router.push({
        query: {
          ...this.$route.query,
          tag: this.filteredTags
        }
      })
    }
  },
  async created () {
    await this.loadData()

    this.loadCampusShuttles()

    // Set the initial campus to St. Paul
    if (!this.activeCampus) {
      this.setActiveCampus(this.campuses.filter((campus) => {
        return campus.code === 'STP'
      })[0])
    }

    this.runRouterCompatibility()
  },
  mounted () {
    try {
      const ustInitCheck = setInterval(() => {
        if (window.ust) {
          window.ust.init()
          clearInterval(ustInitCheck)
          clearTimeout(ustInitTimer)
        }
      }, 200)
      const ustInitTimer = setTimeout(() => {
        console.error('Unable to initialize UST scripts.')
        clearInterval(ustInitCheck)
        clearTimeout(ustInitTimer)
      }, 5000)
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    ...mapActions([
      'setActiveLocations',
      'bindLocations',
      'bindCategories',
      'bindCampuses',
      'bindTransitStops',
      'bindIsDatabaseUp',
      'loadCampusShuttles',
      'loadCampusBounds'
    ]),
    ...mapMutations([
      'setFullscreen',
      'setSelectedLocation',
      'setActiveTransitStop',
      'setActiveCampus',
      'setFilteredTags'
    ]),
    loadData () {
      return Promise.all([
        this.bindLocations(),
        this.bindCategories(),
        this.bindCampuses(),
        this.bindTransitStops(),
        this.bindIsDatabaseUp()
      ])
        .then(() => this.loadCampusBounds())
    },
    async onLocationIdChange (ids) {
      // Check to see if the id is actually coordinates
      if (ids.length === 2 && !isNaN(ids[0]) && !isNaN(ids[1])) {
        const output = (await this.geocode(ids.join(',')))[0]

        this.$root.$emit('create-marker', {
          name: output.name,
          lat: output.center.lat,
          lng: output.center.lng
        })
      } else {
        // Pulls all loctions based on their ids
        const locations = this.locations.filter((location) => {
          return ids.includes(location._id)
        })

        // Reorders the locations to maintain the order defined by the params
        locations.sort((a, b) => {
          return ids.indexOf(a._id) - ids.indexOf(b._id)
        })

        if (locations.length && !this.activeLocations.length && this.activeLocations !== locations) {
          this.setActiveLocations(locations)
        }

        if (locations.length === 1) {
          this.setSelectedLocation(locations[0])
        }
      }
    }
  }
}
</script>

<style lang="scss">
@use "@/styles/vuetify.scss";
@use "@/styles/castor.scss";
@use "@/styles/leaflet.scss";
@use "@/styles/animations.scss";

.v-application {
  font-family: Avenir, Arial, sans-serif !important;

  footer {
    ul, ol {
      padding-left: 0 !important;
    }
  }
}

.application--wrap {
  min-height: 0;
}

#app {
  padding-top: 0;
}

body {
  background-color: var(--v-primary-base);
}

footer, .footer {
  background-color: white;
}

.fullscreen {
  overflow-y: hidden;
}

:root {
  --sat: env(safe-area-inset-top);
  --sar: env(safe-area-inset-right);
  --sab: env(safe-area-inset-bottom);
  --sal: env(safe-area-inset-left);
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: #000000;
  color: white;
  padding: 8px;
  z-index: 100;

  &:focus {
    top: 0;
  }
}

@media only screen and (min-width: 960px) {
  body {
    background-color: white;
  }
}
</style>
